/**
 * AddInventoryDialog (ASS-75)
 *
 * Mark's manual-inventory entry form. Exactly six controls, in order:
 * Truck ID, Project/Yard, Mat Type, Mat Quantity, Cancel, Save.
 * No date/time input -- the server stamps the timestamp; the client sends
 * none.
 *
 * "it should have only the following on it" -- Mark, 2026-09-09.
 * "They can add a negative number, not needed for Friday" -- Mark on
 * ASS-38; a negative quantity is his mechanism for removal (img_03: -6).
 *
 * Mat Type options are read live from the store's asset-subtype list,
 * filtered to the "Mat" parent asset type by name -- never hard-coded
 * (LAT-02/LAT-03: no screen names a customer or a fixed id).
 *
 * Save dispatches createEntryAction with
 * { truckId, matTypeId, quantity, channel: 'desktop', idempotencyKey }
 * and deliberately does NOT refetch afterwards: countSlice's
 * createEntryAction.fulfilled prepends the new item to
 * entriesByProject[projectId] in place, so the panel updates without a
 * round trip. A failed save rejects with the server's message, shown in
 * the dialog's Alert; nothing is stored locally (ASS-64).
 *
 * AST-90 (Mark, 2026-10-07: "it's the add inventory dialog screen"): adds
 * optional From location / To location, below Project/Yard and above
 * Mat Type. Options are the project's active mapped elements (AST-87),
 * grouped Structures / Project/Laydown Yards / Other. Blank is valid and
 * the default; if both are set they must differ. The backend re-checks
 * both (countHandlers.createEntry). Order is Truck ID first, as Mark's
 * original six-control spec had it, then Project/Yard, From, To, Mat Type,
 * Quantity.
 *
 * AST-118: renamed "Enter Asset Move". The Mat Type (subtype) field is
 * replaced by Asset: the project customer's active manual assets (ST124,
 * VT164...) under the Mat asset type, one flat natural-sorted list with
 * type-to-filter, name only. Archived assets and subtype names never appear.
 * Asset and To location are required. The entry carries manualAssetId plus
 * the asset's subtype (matTypeId) and type; the server re-derives both.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  ListSubheader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Alert,
  CircularProgress,
  FormHelperText,
  Autocomplete,
} from '@mui/material';

import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { fetchAssetTypes, fetchAssetSubTypes } from '../../store/slices/assetSlice';
import { createEntryAction } from '../../store/slices/countSlice';
import { fetchAreaElements } from '../../services/areaElementsApi';
import { fetchProjectManualAssets } from '../../services/manualAssetsApi';
import { SECTIONS } from '../../utils/areaElements';

// One UUID per opening of the dialog -- the double-submit guard the ticket
// asks for. Matches the shape InlineCountForm already uses for its own
// idempotency key.
function generateIdempotencyKey() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export default function AddInventoryDialog({ open, onClose, project, onSaved }) {
  const dispatch = useAppDispatch();
  const { assetTypes, assetSubTypes } = useAppSelector((state) => state.asset);
  const { projectList } = useAppSelector((state) => state.project);

  const [truckId, setTruckId] = useState('');
  const [projectId, setProjectId] = useState('');
  // AST-118: the picked manual asset (whole row: id, name, subtypeId, assetTypeId).
  const [manualAsset, setManualAsset] = useState(null);
  const [manualAssets, setManualAssets] = useState({ projectId: null, items: [], loading: false, error: null });
  const [quantity, setQuantity] = useState('');
  // AST-90: optional From / To, and the selected project's mapped elements.
  const [fromElementId, setFromElementId] = useState('');
  const [toElementId, setToElementId] = useState('');
  const [locations, setLocations] = useState({ projectId: null, items: [], loading: false, error: null });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const idempotencyKeyRef = React.useRef(generateIdempotencyKey());

  // Project/Yard dropdown: only projects tracked manual or hybrid ("both") --
  // beacon-only projects have no manual inventory to add to.
  const eligibleProjects = useMemo(
    () => (projectList || []).filter(
      (p) => p.trackingMode === 'manual' || p.trackingMode === 'hybrid'
    ),
    [projectList]
  );

  // Reset the form -- including a fresh idempotency key -- every time the
  // dialog opens, and preselect the current project if it's eligible.
  useEffect(() => {
    if (!open) return;
    setTruckId('');
    setManualAsset(null);
    setQuantity('');
    setFromElementId('');
    setToElementId('');
    setError(null);
    idempotencyKeyRef.current = generateIdempotencyKey();
    setProjectId(
      project?.id && (project.trackingMode === 'manual' || project.trackingMode === 'hybrid')
        ? project.id
        : ''
    );
  }, [open, project?.id, project?.trackingMode]);

  // AST-90: load the selected project's mapped locations. Changing Project
  // reloads them and clears any From/To that doesn't belong to the new one.
  useEffect(() => {
    if (!open || !projectId) {
      setLocations({ projectId: null, items: [], loading: false, error: null });
      return undefined;
    }
    let cancelled = false;
    setLocations((l) => ({ ...l, projectId, loading: true, error: null }));
    fetchAreaElements(projectId)
      .then((res) => {
        if (cancelled) return;
        const items = (res?.items || []).filter((e) => !e.retired);
        setLocations({ projectId, items, loading: false, error: null });
        const ids = new Set(items.map((e) => e.id));
        setFromElementId((v) => (v && !ids.has(v) ? '' : v));
        setToElementId((v) => (v && !ids.has(v) ? '' : v));
      })
      .catch(() => {
        if (cancelled) return;
        // Non-fatal: the entry can still be saved at project level.
        setLocations({ projectId, items: [], loading: false, error: "Couldn't load this project's locations." });
        setFromElementId('');
        setToElementId('');
      });
    return () => { cancelled = true; };
  }, [open, projectId]);

  const locationGroups = useMemo(() => SECTIONS
    .map(({ key, label }) => ({
      key,
      label: key === 'yards' ? 'Yards' : label,
      items: locations.items.filter((e) => e.section === key).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
    }))
    .filter((g) => g.items.length > 0), [locations.items]);
  const hasLocations = locationGroups.length > 0;

  // Load the catalog once if it isn't already in the store (e.g. a fresh
  // session that hasn't visited AssetPage yet).
  useEffect(() => {
    if (!open) return;
    if (!assetTypes || assetTypes.length === 0) dispatch(fetchAssetTypes());
    if (!assetSubTypes || assetSubTypes.length === 0) dispatch(fetchAssetSubTypes());
  }, [open, assetTypes, assetSubTypes, dispatch]);

  // The Mat asset type, resolved by name, not a hard-coded id -- the catalog
  // is customer data.
  const matAssetTypeId = useMemo(
    () => (assetTypes || []).find((t) => t.name === 'Mat')?.id || null,
    [assetTypes]
  );

  // AST-118: the selected project's customer's manual assets. Changing
  // Project/Yard reloads the list and clears the selection.
  useEffect(() => {
    setManualAsset(null);
    if (!open || !projectId) {
      setManualAssets({ projectId: null, items: [], loading: false, error: null });
      return undefined;
    }
    let cancelled = false;
    setManualAssets({ projectId, items: [], loading: true, error: null });
    fetchProjectManualAssets(projectId)
      .then((res) => {
        if (!cancelled) setManualAssets({ projectId, items: res?.items || [], loading: false, error: null });
      })
      .catch(() => {
        if (!cancelled) setManualAssets({ projectId, items: [], loading: false, error: "Couldn't load this project's assets." });
      });
    return () => { cancelled = true; };
  }, [open, projectId]);

  // Active Mat assets only, one flat list, natural A->Z (ST2 before ST10).
  const assetOptions = useMemo(() => {
    if (!matAssetTypeId) return [];
    return manualAssets.items
      .filter((a) => !a.archived && a.assetTypeId === matAssetTypeId && a.name)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
  }, [manualAssets.items, matAssetTypeId]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [submitting, onClose]);

  const quantityNum = quantity === '' ? null : Number(quantity);
  const isQuantityValid = quantityNum !== null
    && !Number.isNaN(quantityNum)
    && Number.isInteger(quantityNum)
    && quantityNum !== 0;
  const sameFromTo = fromElementId !== '' && fromElementId === toElementId;
  // AST-101 (Mark): "Not required is the right answer. Some organizations may
  // just want aggregate reporting where others may want it tracked by truck."
  // So Truck ID is optional; Project/Yard, Mat Type and Quantity stay
  // required (Mark confirmed From/To stay optional too).
  // AST-118 (AM-8): Asset and To location are required too.
  const isFormValid = projectId !== ''
    && manualAsset !== null
    && toElementId !== ''
    && isQuantityValid
    && !sameFromTo;

  const handleSave = useCallback(async () => {
    if (!isFormValid) return;
    setSubmitting(true);
    setError(null);
    try {
      await dispatch(createEntryAction({
        projectId,
        data: {
          manualAssetId: manualAsset.id,
          matTypeId: manualAsset.subtypeId,
          assetTypeId: manualAsset.assetTypeId,
          quantity: quantityNum,
          channel: 'desktop',
          idempotencyKey: idempotencyKeyRef.current,
          // AST-101: Truck ID is optional -- omit it when blank rather than
          // sending '', so the backend stores no attribute at all.
          ...(truckId.trim() ? { truckId: truckId.trim() } : {}),
          ...(fromElementId ? { fromElementId } : {}),
          ...(toElementId ? { toElementId } : {}),
        },
      })).unwrap();
      onSaved?.('Asset move saved');
      onClose?.();
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Could not save this entry. Try again.');
    } finally {
      setSubmitting(false);
    }
  }, [isFormValid, projectId, truckId, manualAsset, quantityNum, fromElementId, toElementId, dispatch, onSaved, onClose]);

  // One grouped Select for each of From / To. MUI Select needs flat
  // children, so the group headers are ListSubheader siblings.
  const renderLocationSelect = (id, label, value, setValue) => {
    const disabled = submitting || !projectId || locations.loading || !hasLocations;
    let helper = ' ';
    if (projectId && !locations.loading && !hasLocations) helper = locations.error || 'No locations mapped';
    return (
      <FormControl fullWidth disabled={disabled}>
        <InputLabel id={`${id}-label`} shrink={!hasLocations ? true : undefined}>{label}</InputLabel>
        <Select
          labelId={`${id}-label`}
          label={label}
          value={value}
          displayEmpty={!hasLocations}
          onChange={(e) => setValue(e.target.value)}
          renderValue={(v) => (v ? locations.items.find((e) => e.id === v)?.name || '' : '')}
          inputProps={{ 'aria-describedby': `${id}-helper`, 'data-testid': `${id}-value` }}
        >
          <MenuItem value=""><em>None</em></MenuItem>
          {locationGroups.flatMap((g) => [
            // MUI Select forces role="option" onto every child, so a plain
            // ListSubheader would be announced as a choice. Make the header
            // a disabled, non-selectable item, and name each option with its
            // group so a screen reader hears "Str 001, Structures".
            <ListSubheader key={`${id}-h-${g.key}`} aria-disabled="true" onClickCapture={(ev) => ev.stopPropagation()} sx={{ lineHeight: '32px' }}>{g.label}</ListSubheader>,
            ...g.items.map((e) => (
              <MenuItem key={`${id}-${e.id}`} value={e.id} aria-label={`${e.name}, ${g.label}`}>{e.name}</MenuItem>
            )),
          ])}
        </Select>
        <FormHelperText id={`${id}-helper`}>{helper}</FormHelperText>
      </FormControl>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enter Asset Move</DialogTitle>
      {/* AST-101 (Mark): "the label for Truck ID is scrolled off the top".
          DialogContent is the scroll container and Truck ID autofocuses, so
          the browser scrolls the field into view on open -- and an outlined
          TextField's label sits *above* the input's top border, so it goes
          out of view unless there's room for it.

          Two traps here, both found by measuring the live page:

          1. MUI emits `.MuiDialogTitle-root + .MuiDialogContent-root {
             padding-top: 0 }`. That selector is more specific than a plain
             `pt`, so `pt: 3` silently did nothing. `&&` doubles the class
             specificity so ours wins.
          2. This theme sets `spacing: 4`, so a spacing unit is 4px, not 8.
             `pt: 3` would be 12px even if it applied. The label needs ~11px
             of its own height, so use an explicit pixel value rather than a
             multiplier that reads like 24px but isn't. */}
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          '&&': { paddingTop: '20px' },
        }}
      >
        <TextField
          label="Truck ID"
          value={truckId}
          onChange={(e) => setTruckId(e.target.value)}
          fullWidth
          disabled={submitting}
          autoFocus
          helperText="Optional"
        />

        <FormControl fullWidth disabled={submitting}>
          <InputLabel id="add-inventory-project-label">Project/Yard</InputLabel>
          <Select
            labelId="add-inventory-project-label"
            label="Project/Yard"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            {eligibleProjects.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {renderLocationSelect('add-inventory-from', 'From location', fromElementId, setFromElementId)}
        {renderLocationSelect('add-inventory-to', 'To location', toElementId, setToElementId)}
        {sameFromTo && <Alert severity="error">From and To must be different.</Alert>}

        <Autocomplete
          id="add-inventory-asset"
          options={assetOptions}
          value={manualAsset}
          onChange={(_e, v) => setManualAsset(v)}
          getOptionLabel={(o) => o?.name || ''}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          disabled={submitting || !projectId}
          loading={manualAssets.loading}
          noOptionsText="No matching assets"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Asset"
              required
              helperText={
                projectId && !manualAssets.loading && assetOptions.length === 0
                  ? manualAssets.error || 'No manual assets set up'
                  : ' '
              }
            />
          )}
        />

        <TextField
          label="Mat Quantity"
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          fullWidth
          disabled={submitting}
          helperText="Negative removes mats from the count."
          inputProps={{ step: 1 }}
        />

        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isFormValid || submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
          sx={{ textTransform: 'none' }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
